import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AppWindow,
  Boxes,
  Building2,
  CheckCircle2,
  CreditCard,
  Hash,
  Layers,
  Shield,
  UserCheck,
  Users,
  XCircle,
} from "lucide-react";

const ICON_MAP = {
  users: Users,
  building: Building2,
  payment: CreditCard,
  layers: Layers,
  shield: Shield,
  apps: AppWindow,
  boxes: Boxes,
  userCheck: UserCheck,
  hash: Hash,
  check: CheckCircle2,
  x: XCircle,
};

export function distinctCount(rows, key) {
  const seen = new Set();
  (rows || []).forEach((row) => {
    const value = row?.[key];
    if (value == null || String(value).trim() === "") return;
    seen.add(String(value));
  });
  return seen.size;
}

export function countWhere(rows, predicate) {
  return (rows || []).filter((row) => {
    try {
      return Boolean(predicate(row));
    } catch {
      return false;
    }
  }).length;
}

export function formatCardValue(value) {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isFinite(num)) return num.toLocaleString("en-IN");
  return String(value);
}

export default function ListSummaryCards({
  cards = [],
  style,
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const resolved = useMemo(
    () => (Array.isArray(cards) ? cards.filter(Boolean) : []),
    [cards]
  );

  if (!resolved.length) return null;

  const isActive = (card) => {
    if (card.active === true) return true;
    if (card.active === false) return false;
    const query = card.query;
    if (!query || typeof query !== "object") return false;
    const entries = Object.entries(query).filter(
      ([key]) => key !== "page" && key !== "pageSize"
    );
    if (!entries.length) {
      return !Array.from(searchParams.keys()).some((key) =>
        key.startsWith("filter_")
      );
    }
    return entries.every(([key, value]) => {
      const expected = String(value ?? "").trim().toLowerCase();
      return (
        String(searchParams.get(key) || "")
          .trim()
          .toLowerCase() === expected
      );
    });
  };

  const applyCard = (card) => {
    if (typeof card.onClick === "function") {
      card.onClick(card);
      return;
    }
    if (!card.query || typeof card.query !== "object") return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(card.query).forEach(([key, value]) => {
        if (value == null || value === "") next.delete(key);
        else next.set(key, String(value));
      });
      next.set("page", "1");
      return next;
    });
  };

  return (
    <section
      className="ems-stat-cards-brand ems-list-widgets"
      style={{ marginBottom: 16, ...style }}
    >
      <div
        className={`ems-grid ${
          resolved.length >= 5 ? "ems-g-5" : "ems-g-4"
        }`}
      >
        {resolved.map((card) => {
          const active = isActive(card);
          const Icon = card.icon
            ? card.icon
            : ICON_MAP[card.iconKey] || Boxes;
          const clickable = Boolean(card.onClick || card.query);
          return (
            <div
              key={card.key || card.label}
              className={`ems-card ems-stat${active ? " is-active" : ""}`}
              style={{ cursor: clickable ? "pointer" : "default" }}
              onClick={clickable ? () => applyCard(card) : undefined}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              onKeyDown={
                clickable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        applyCard(card);
                      }
                    }
                  : undefined
              }
            >
              <div className="ems-stat-top">
                <div
                  className={`ems-stat-icon${
                    card.tone ? ` ${card.tone}` : ""
                  }`}
                >
                  <Icon size={20} />
                </div>
              </div>
              <div className="ems-stat-label">{card.label}</div>
              <div
                className={`ems-stat-value ems-tnum${
                  card.valueTone ? ` ${card.valueTone}` : ""
                }`}
              >
                {formatCardValue(card.value)}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
